(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const REQUIRED = [
    'first_name',
    'last_name',
    'email',
    'company',
    'ticket_type',
    'country',
    'city',
    'calling_code',
    'phone'
  ];

  // Calling codes from libphonenumber-js 1.11.20 (MIT), bundled locally.
  const CALLING_CODES = {
    AC: '+247', AD: '+376', AE: '+971', AF: '+93',
    AG: '+1', AI: '+1', AL: '+355', AM: '+374',
    AO: '+244', AR: '+54', AS: '+1', AT: '+43',
    AU: '+61', AW: '+297', AX: '+358', AZ: '+994',
    BA: '+387', BB: '+1', BD: '+880', BE: '+32',
    BF: '+226', BG: '+359', BH: '+973', BI: '+257',
    BJ: '+229', BL: '+590', BM: '+1', BN: '+673',
    BO: '+591', BQ: '+599', BR: '+55', BS: '+1',
    BT: '+975', BW: '+267', BY: '+375', BZ: '+501',
    CA: '+1', CC: '+61', CD: '+243', CF: '+236',
    CG: '+242', CH: '+41', CI: '+225', CK: '+682',
    CL: '+56', CM: '+237', CN: '+86', CO: '+57',
    CR: '+506', CU: '+53', CV: '+238', CW: '+599',
    CX: '+61', CY: '+357', CZ: '+420', DE: '+49',
    DJ: '+253', DK: '+45', DM: '+1', DO: '+1',
    DZ: '+213', EC: '+593', EE: '+372', EG: '+20',
    EH: '+212', ER: '+291', ES: '+34', ET: '+251',
    FI: '+358', FJ: '+679', FK: '+500', FM: '+691',
    FO: '+298', FR: '+33', GA: '+241', GB: '+44',
    GD: '+1', GE: '+995', GF: '+594', GG: '+44',
    GH: '+233', GI: '+350', GL: '+299', GM: '+220',
    GN: '+224', GP: '+590', GQ: '+240', GR: '+30',
    GT: '+502', GU: '+1', GW: '+245', GY: '+592',
    HK: '+852', HN: '+504', HR: '+385', HT: '+509',
    HU: '+36', ID: '+62', IE: '+353', IL: '+972',
    IM: '+44', IN: '+91', IO: '+246', IQ: '+964',
    IR: '+98', IS: '+354', IT: '+39', JE: '+44',
    JM: '+1', JO: '+962', JP: '+81', KE: '+254',
    KG: '+996', KH: '+855', KI: '+686', KM: '+269',
    KN: '+1', KP: '+850', KR: '+82', KW: '+965',
    KY: '+1', KZ: '+7', LA: '+856', LB: '+961',
    LC: '+1', LI: '+423', LK: '+94', LR: '+231',
    LS: '+266', LT: '+370', LU: '+352', LV: '+371',
    LY: '+218', MA: '+212', MC: '+377', MD: '+373',
    ME: '+382', MF: '+590', MG: '+261', MH: '+692',
    MK: '+389', ML: '+223', MM: '+95', MN: '+976',
    MO: '+853', MP: '+1', MQ: '+596', MR: '+222',
    MS: '+1', MT: '+356', MU: '+230', MV: '+960',
    MW: '+265', MX: '+52', MY: '+60', MZ: '+258',
    NA: '+264', NC: '+687', NE: '+227', NF: '+672',
    NG: '+234', NI: '+505', NL: '+31', NO: '+47',
    NP: '+977', NR: '+674', NU: '+683', NZ: '+64',
    OM: '+968', PA: '+507', PE: '+51', PF: '+689',
    PG: '+675', PH: '+63', PK: '+92', PL: '+48',
    PM: '+508', PR: '+1', PS: '+970', PT: '+351',
    PW: '+680', PY: '+595', QA: '+974', RE: '+262',
    RO: '+40', RS: '+381', RU: '+7', RW: '+250',
    SA: '+966', SB: '+677', SC: '+248', SD: '+249',
    SE: '+46', SG: '+65', SH: '+290', SI: '+386',
    SJ: '+47', SK: '+421', SL: '+232', SM: '+378',
    SN: '+221', SO: '+252', SR: '+597', SS: '+211',
    ST: '+239', SV: '+503', SX: '+1', SY: '+963',
    SZ: '+268', TA: '+290', TC: '+1', TD: '+235',
    TG: '+228', TH: '+66', TJ: '+992', TK: '+690',
    TL: '+670', TM: '+993', TN: '+216', TO: '+676',
    TR: '+90', TT: '+1', TV: '+688', TW: '+886',
    TZ: '+255', UA: '+380', UG: '+256', US: '+1',
    UY: '+598', UZ: '+998', VA: '+39', VC: '+1',
    VE: '+58', VG: '+1', VI: '+1', VN: '+84',
    VU: '+678', WF: '+681', WS: '+685', XK: '+383',
    YE: '+967', YT: '+262', ZA: '+27', ZM: '+260',
    ZW: '+263'
  };

  const countryNames = new Intl.DisplayNames(['en'], {
    type: 'region'
  });

  const nameToIso = Object.fromEntries(
    Object.keys(CALLING_CODES).map(code => [
      countryNames.of(code).toLowerCase(),
      code
    ])
  );

  Object.assign(nameToIso, {
    'united states': 'US',
    'united states of america': 'US',
    'united kingdom': 'GB',
    'czech republic': 'CZ',
    'cape verde': 'CV',
    'swaziland': 'SZ',
    'east timor': 'TL',
    'macedonia, republic of': 'MK',
    'macedonia': 'MK',
    'taiwan': 'TW',
    'south korea': 'KR',
    'north korea': 'KP',
    'russia': 'RU',
    'iran': 'IR',
    'syria': 'SY',
    'laos': 'LA',
    'vietnam': 'VN',
    'moldova': 'MD',
    'bolivia': 'BO',
    'venezuela': 'VE',
    'tanzania': 'TZ',
    'brunei': 'BN',
    'palestinian territory, occupied': 'PS',
    'palestine, state of': 'PS',
    'congo, the democratic republic of the': 'CD',
    'congo': 'CG',
    'cote d’ivoire': 'CI',
    "cote d'ivoire": 'CI',
    "côte d'ivoire": 'CI',
    'reunion': 'RE',
    'curacao': 'CW',
    'virgin islands, british': 'VG',
    'virgin islands, u.s.': 'VI',
    'saint martin (french part)': 'MF',
    'sint maarten (dutch part)': 'SX',
    'saint barthelemy': 'BL',
    'saint kitts and nevis': 'KN',
    'saint lucia': 'LC',
    'saint vincent and the grenadines': 'VC',
    'sao tome and principe': 'ST',
    'turkey': 'TR',
    'kosovo': 'XK',
    'hong kong': 'HK',
    'macao': 'MO'
  });

  const US_STATES = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming'
  ];

  const normalizeEmail = (value) =>
    String(value || '').trim().toLowerCase();

  const identifier = (prefix) => {
    if (window.crypto?.randomUUID) {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }

    return `${prefix}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 12)}`;
  };

  class PjxGroupRegistration {
    constructor(root) {
      if (root.dataset.pjxInitialized === 'true') return;
      root.dataset.pjxInitialized = 'true';

      this.root = root;
      this.variantId = Number(root.dataset.variantId || 0);
      this.expectedSku = root.dataset.expectedSku || '363102';
      this.selectedSku = root.dataset.selectedSku || '';
      this.max = Math.max(
        1,
        Number(root.dataset.maxAttendees || 100)
      );

      this.groupId = identifier('pjx_group');
      this.attendees = [];
      this.container = root.querySelector('[data-pjx-attendees]');
      this.template = root.querySelector('[data-pjx-attendee-template]');
      this.form = root.querySelector('[data-pjx-form]');
      this.entryStep = root.querySelector('[data-pjx-entry-step]');
      this.reviewStep = root.querySelector('[data-pjx-review-step]');
      this.reviewList = root.querySelector('[data-pjx-review-list]');
      this.entryError = root.querySelector('[data-pjx-error]');
      this.reviewError = root.querySelector('[data-pjx-review-error]');
      this.policy = root.querySelector('[data-pjx-policy]');

      if (!this.form || !this.template || !this.variantId) return;

      this.addAttendeeButton = root.querySelector(
        '[data-pjx-add-attendee]'
      );

      this.addAttendeeButton?.addEventListener('click', (event) =>
        this.addAttendee(null, event.currentTarget)
      );

      this.container.addEventListener('click', (event) => {
        const removeButton = event.target.closest(
          '[data-pjx-remove-attendee]'
        );

        if (removeButton) {
          this.removeAttendee(removeButton);
        }
      });

      this.container.addEventListener('change', (event) => {
        if (!event.target.matches('[data-pjx-country]')) return;

        this.configureLocation(
          event.target.closest('[data-pjx-attendee]'),
          true
        );
      });

      root.querySelector('[data-pjx-edit]')?.addEventListener(
        'click',
        () => this.showEntry()
      );

      root.querySelector('[data-pjx-add]')?.addEventListener(
        'click',
        (event) => this.addToCart(event.currentTarget)
      );

      this.form.addEventListener(
        'submit',
        (event) => this.review(event)
      );

      this.addAttendee();
    }

    currentValues() {
      return [
        ...this.container.querySelectorAll('[data-pjx-attendee]')
      ].map((fieldset) => {
        const data = {
          id: fieldset.dataset.attendeeId ||
            identifier('pjx_attendee')
        };

        fieldset.querySelectorAll(
          '[data-field]:not(:disabled)'
        ).forEach((field) => {
          data[field.dataset.field] = field.value;
        });

        return data;
      });
    }

    addAttendee(saved = null, trigger = null) {
      const count = this.container.querySelectorAll(
        '[data-pjx-attendee]'
      ).length;

      if (count >= this.max) return;

      const fragment = this.template.content.cloneNode(true);
      const fieldset = fragment.querySelector('[data-pjx-attendee]');
      const attendee = saved || {
        id: identifier('pjx_attendee')
      };

      fieldset.dataset.attendeeId = attendee.id;

      fieldset.querySelectorAll('[data-field]').forEach((field) => {
        const key = field.dataset.field;

        if (attendee[key] != null) {
          field.value = attendee[key];
        }
      });

            const countrySelect = fieldset.querySelector('[data-pjx-country]');

      if (countrySelect) {
        const unitedStates = [...countrySelect.options].find(
          option => option.value === 'United States'
        );

        if (unitedStates) {
          const firstCountry = [...countrySelect.options].find(
            option => option.value !== ''
          );

          if (firstCountry && firstCountry !== unitedStates) {
            countrySelect.insertBefore(unitedStates, firstCountry);
          }
        }
      }

      this.container.append(fragment);
      this.configureLocation(fieldset, !saved);
      this.renumberForms();
      this.hideError(this.entryError);

      if (trigger) {
        fieldset.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        fieldset.querySelector('[data-field]')?.focus({
          preventScroll: true
        });
      }
    }

    removeAttendee(button) {
      const fieldsets = this.container.querySelectorAll(
        '[data-pjx-attendee]'
      );

      if (fieldsets.length <= 1) return;

      button.closest('[data-pjx-attendee]')?.remove();
      this.renumberForms();
      this.hideError(this.entryError);
    }

    renumberForms() {
      const fieldsets = [
        ...this.container.querySelectorAll('[data-pjx-attendee]')
      ];

      fieldsets.forEach((fieldset, index) => {
        fieldset.querySelector(
          '[data-pjx-number]'
        ).textContent = String(index + 1);

        const removeButton = fieldset.querySelector(
          '[data-pjx-remove-attendee]'
        );

        if (removeButton) {
          removeButton.hidden = index === 0;
        }

        const fieldCounts = {};

        fieldset.querySelectorAll('[data-field]').forEach((field) => {
          const key = field.dataset.field;
          fieldCounts[key] = (fieldCounts[key] || 0) + 1;

          field.name = `attendees[${index}][${key}]`;
          field.id = `pjx-attendee-${index}-${key}-${fieldCounts[key]}`;

          const label = field.closest(
            '.pjx27-field'
          )?.querySelector('label');

          if (label && !field.disabled) {
            label.htmlFor = field.id;
          }
        });
      });

      if (this.addAttendeeButton) {
        const atLimit = fieldsets.length >= this.max;
        this.addAttendeeButton.disabled = atLimit;
        this.addAttendeeButton.setAttribute(
          'aria-disabled',
          String(atLimit)
        );
      }
    }

    configureLocation(fieldset, changed) {
      const country = fieldset.querySelector('[data-pjx-country]');
      const select = fieldset.querySelector('[data-pjx-region-select]');
      const input = fieldset.querySelector('[data-pjx-region-input]');
      const label = fieldset.querySelector('[data-pjx-region-label]');
      const calling = fieldset.querySelector('[data-pjx-calling-code]');

      if (!country || !select || !input) return;

      const option = country.options[country.selectedIndex];
      const raw = country.value.trim();

      const iso = CALLING_CODES[raw.toUpperCase()]
        ? raw.toUpperCase()
        : nameToIso[raw.toLowerCase()] ||
          nameToIso[
            (option?.textContent || '').trim().toLowerCase()
          ];

      const previous = select.disabled
        ? input.value
        : select.value;

      const us = iso === 'US';

      select.replaceChildren(new Option('Select state', ''));

      US_STATES.forEach(state => {
        select.add(new Option(state, state));
      });

      select.hidden = select.disabled = !us;
      select.required = us;

      input.hidden = input.disabled = us;
      input.required = false;

      select.value = changed ? '' : previous;
      input.value = changed ? '' : previous;

      if (label) {
        label.textContent = us
          ? 'State *'
          : 'State / province / region (if applicable)';

        label.htmlFor = (us ? select : input).id;
      }

      if (calling) {
        calling.value = CALLING_CODES[iso] || '';
        calling.readOnly = Boolean(CALLING_CODES[iso]);
        calling.placeholder = '+Code';
      }
    }

    validate(attendees) {
      for (let index = 0; index < attendees.length; index += 1) {
        const attendee = attendees[index];

        for (const key of REQUIRED) {
          if (!String(attendee[key] || '').trim()) {
            return `Complete every required field for attendee ${index + 1}.`;
          }
        }

        const emailField = this.container.querySelectorAll(
          '[data-pjx-attendee]'
        )[index]?.querySelector('[data-field="email"]');

        if (emailField && !emailField.validity.valid) {
          return `Enter a valid email for attendee ${index + 1}.`;
        }

        const fieldset = this.container.querySelectorAll(
          '[data-pjx-attendee]'
        )[index];

        const invalidField = fieldset?.querySelector(
          '[data-field]:not(:disabled):invalid'
        );

        if (invalidField) {
          return `Check the highlighted contact or location field for attendee ${index + 1}.`;
        }

        if (!/^[\p{L}\p{M} .'-]{2,80}$/u.test(attendee.city)) {
          return `Enter a valid city or locality for attendee ${index + 1}.`;
        }

        const callingDigits = String(
          attendee.calling_code || ''
        ).replace(/\D/g, '');

        const phoneDigits = String(
          attendee.phone || ''
        ).replace(/\D/g, '');

        if (
          !/^\+?\d{1,4}$/.test(attendee.calling_code) ||
          phoneDigits.length < 6 ||
          (callingDigits + phoneDigits).length > 15
        ) {
          return `Enter a valid international phone number for attendee ${index + 1}.`;
        }
      }

      const emails = attendees.map(
        (attendee) => normalizeEmail(attendee.email)
      );

      if (new Set(emails).size !== emails.length) {
        return 'Every attendee must use a unique email address.';
      }

      return '';
    }

    async review(event) {
      event.preventDefault();
      this.hideError(this.entryError);

      if (this.selectedSku !== this.expectedSku) {
        this.showError(
          this.entryError,
          `Registration setup is incomplete. The selected product must use SKU ${this.expectedSku}.`
        );
        return;
      }

      const attendees = this.currentValues().map((attendee) => {
        Object.keys(attendee).forEach((key) => {
          if (typeof attendee[key] === 'string') {
            attendee[key] = attendee[key].trim();
          }
        });

        return attendee;
      });

      const error = this.validate(attendees);

      if (error) {
        this.showError(this.entryError, error);
        return;
      }

      try {
        const cart = await this.fetchCart();

        const cartEmails = new Set(
          (cart.items || [])
            .filter(
              (item) =>
                item.properties?._pjx_registration === 'marketplace_2027'
            )
            .map(
              (item) =>
                normalizeEmail(item.properties?.['Attendee email'])
            )
            .filter(Boolean)
        );

        const duplicate = attendees.find(
          (attendee) =>
            cartEmails.has(normalizeEmail(attendee.email))
        );

        if (duplicate) {
          this.showError(
            this.entryError,
            `${duplicate.email} is already attached to a PJX ticket in this cart.`
          );
          return;
        }
      } catch (error) {
        this.showError(
          this.entryError,
          'We could not verify the cart. Refresh the page and try again.'
        );
        return;
      }

      this.attendees = attendees;
      this.renderReview();
      this.entryStep.hidden = true;
      this.reviewStep.hidden = false;

      this.reviewStep.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    renderReview() {
      this.reviewList.replaceChildren();

      this.attendees.forEach((attendee, index) => {
        const card = document.createElement('article');
        card.className = 'pjx27-register__review-card';

        const name = document.createElement('strong');
        name.textContent =
          `${index + 1}. ${attendee.first_name} ${attendee.last_name}`;

        const primary = document.createElement('span');
        primary.textContent =
          `${attendee.email} · ${attendee.company} · ${attendee.ticket_type}`;

        const secondary = document.createElement('span');
        const phone =
          `${attendee.calling_code} ${attendee.phone}`.trim();

        const optional = [
          attendee.badge_name && `Badge: ${attendee.badge_name}`,
          phone,
          attendee.city,
          attendee.region,
          attendee.country
        ].filter(Boolean);

        secondary.textContent = optional.join(' · ') ||
          'No optional contact details provided';

        card.append(name, primary, secondary);
        this.reviewList.append(card);
      });

      const total = document.createElement('p');
      total.innerHTML =
        `<strong>${this.attendees.length} ticket${this.attendees.length === 1 ? '' : 's'} · $${this.attendees.length * 25} total</strong>`;

      this.reviewList.append(total);
    }

    showEntry() {
      this.reviewStep.hidden = true;
      this.entryStep.hidden = false;
      this.policy.checked = false;
      this.hideError(this.reviewError);

      this.entryStep.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    properties(attendee) {
      const properties = {
        'Attendee first name': attendee.first_name,
        'Attendee last name': attendee.last_name,
        'Attendee email': attendee.email,
        'Company / business': attendee.company,
        'Ticket type': attendee.ticket_type,
        '_pjx_registration': 'marketplace_2027',
        '_pjx_attendee_id': attendee.id,
        '_pjx_group_id': this.groupId,
        '_pjx_expected_sku': this.expectedSku,
        '_pjx_schema_version': '2',
        '_pjx_non_refundable': 'true'
      };

      const optional = {
        'Phone': `${attendee.calling_code} ${attendee.phone}`.trim(),
        'City': attendee.city,
        'State': attendee.region,
        'Country': attendee.country,
        'Dietary restrictions': attendee.dietary,
        'Accessibility needs': attendee.accessibility,
        'Badge name': attendee.badge_name
      };

      Object.entries(optional).forEach(([key, value]) => {
        if (String(value || '').trim()) {
          properties[key] = String(value).trim();
        }
      });

      return properties;
    }

    async addToCart(button) {
      this.hideError(this.reviewError);

      if (!this.policy.checked) {
        this.showError(
          this.reviewError,
          'Confirm the non-refundable ticket policy before continuing.'
        );
        return;
      }

      if (!this.attendees.length) {
        this.showError(
          this.reviewError,
          'Return to the attendee form and review your group.'
        );
        return;
      }

      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding tickets…';
      this.root.classList.add('pjx27-register__loader');

      try {
        const response = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: this.attendees.map((attendee) => ({
              id: this.variantId,
              quantity: 1,
              properties: this.properties(attendee)
            }))
          })
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));

          throw new Error(
            body.description ||
            body.message ||
            'Unable to add tickets.'
          );
        }

        window.location.assign(`${ROOT}cart`);
      } catch (error) {
        this.showError(
          this.reviewError,
          error.message ||
            'Unable to add tickets. Please try again.'
        );

        button.disabled = false;
        button.textContent = original;
        this.root.classList.remove('pjx27-register__loader');
      }
    }

    async fetchCart() {
      const response = await fetch(`${ROOT}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to load cart.');
      }

      return response.json();
    }

    showError(element, message) {
      if (!element) return;

      element.textContent = message;
      element.hidden = false;

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    hideError(element) {
      if (!element) return;

      element.hidden = true;
      element.textContent = '';
    }
  }

  const initialize = () =>
    document.querySelectorAll(
      '[data-pjx-registration]'
    ).forEach((root) => new PjxGroupRegistration(root));

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();